// Mirror of the evaluation constants in src/misc/enumerations.js, locked by src/tests/evaluationContract.spec.js.

export const EvaluationDirection = {
  WeRate: 'weRate',
  TheyRateUs: 'theyRateUs',
} as const;

export const EvaluationContextType = {
  Project: 'project',
  Historical: 'historical',
  None: 'none',
} as const;

export const EvaluationSource = {
  Wizard: 'wizard',
  Quick: 'quick',
  ErfaAdapter: 'erfaAdapter',
  Legacy: 'legacy',
} as const;

export const EvaluationQuestionType = {
  Rating: 'rating',
  Text: 'text',
  Paragraph: 'paragraph',
} as const;

export const EvaluationCommentRule = {
  None: 'none',
  Below3: 'below3',
  Always: 'always',
} as const;

export const EvaluationRequestStatus = {
  Active: 'active',
  Overdue: 'overdue',
  Completed: 'completed',
  Closed: 'closed',
  Cancelled: 'cancelled',
} as const;

export const EvaluationRecipientStatus = {
  Revoked: 'revoked',
  Answered: 'answered',
  Declined: 'declined',
  Closed: 'closed',
  Failed: 'failed',
  Overdue: 'overdue',
  Opened: 'opened',
  Sent: 'sent',
  NotSent: 'notSent',
} as const;

export const EvaluationEventType = {
  MigrationStarted: 'migrationStarted',
  MigrationCompleted: 'migrationCompleted',
  MigrationReverted: 'migrationReverted',
  BatchCreated: 'batchCreated',
  ResponseSubmitted: 'responseSubmitted',
  RecipientDeclined: 'recipientDeclined',
  ResponseHidden: 'responseHidden',
  LinkOpened: 'linkOpened',
  MigrationAborted: 'migrationAborted',
} as const;

export const EvaluationLimits = {
  ScaleMin: 1,
  ScaleMax: 5,
  CommentRequiredBelow: 3,
  MaxTagsPerRequest: 5,
  MaxSuppliersPerSend: 10,
  MaxRecipientsPerRequest: 50,
  MaxQuestionsPerRequest: 40,
  TagNameMaxLength: 40,
  CommentMaxLength: 2000,
  TextAnswerMaxLength: 4000,
  MessageMaxLength: 4000,
  HideReasonMaxLength: 500,
  IdempotencyKeyMaxLength: 100,
} as const;

export const EvaluationErrors = {
  MigrationPending: 'evaluation.migrationPending',
  NotFound: 'evaluation.notFound',
  Closed: 'evaluation.closed',
  AlreadyAnswered: 'evaluation.alreadyAnswered',
  InvalidAnswers: 'evaluation.invalidAnswers',
  NoAnswers: 'evaluation.noAnswers',
  TagNotFound: 'evaluation.tagNotFound',
  TagArchived: 'evaluation.tagArchived',
  TagNameTaken: 'evaluation.tagNameTaken',
  TagNameInvalid: 'evaluation.tagNameInvalid',
  SupplierNotEvaluable: 'evaluation.supplierNotEvaluable',
  RecipientIsSupplierContact: 'evaluation.recipientIsSupplierContact',
  DuplicateRecipient: 'evaluation.duplicateRecipient',
  TooManySuppliers: 'evaluation.tooManySuppliers',
  TooManyRecipients: 'evaluation.tooManyRecipients',
  TooManyQuestions: 'evaluation.tooManyQuestions',
  TooManyTags: 'evaluation.tooManyTags',
  InvalidContext: 'evaluation.invalidContext',
  InvalidQuestions: 'evaluation.invalidQuestions',
  MessageTooLong: 'evaluation.messageTooLong',
  NoRecipients: 'evaluation.noRecipients',
  BatchInProgress: 'evaluation.batchInProgress',
  HideReasonRequired: 'evaluation.hideReasonRequired',
  LegacyLinkMoved: 'evaluation.legacyLinkMoved',
  RateLimited: 'evaluation.rateLimited',
} as const;

type ValueOf<T> = T[keyof T];

export type EvaluationDirectionValue = ValueOf<typeof EvaluationDirection>;
export type EvaluationContextTypeValue = ValueOf<typeof EvaluationContextType>;
export type EvaluationSourceValue = ValueOf<typeof EvaluationSource>;
export type EvaluationQuestionTypeValue = ValueOf<typeof EvaluationQuestionType>;
export type EvaluationCommentRuleValue = ValueOf<typeof EvaluationCommentRule>;
export type EvaluationRequestStatusValue = ValueOf<typeof EvaluationRequestStatus>;
export type EvaluationRecipientStatusValue = ValueOf<typeof EvaluationRecipientStatus>;
export type EvaluationEventTypeValue = ValueOf<typeof EvaluationEventType>;
export type EvaluationErrorKey = ValueOf<typeof EvaluationErrors>;
